import { useEffect, useState, useCallback } from 'react'
import type { Page } from '../api/client'
import { agentCommissionsApi, type AgentCommission } from '../api/finance-ext'
import { fetchMe, type LoginResponse } from '../api/auth'
import { SortKeyTh, SortResetLink } from '../components/SortKeyTh'
import {
  StatGrid, Page as PageLayout, PageHeader, Select, StatCard, Callout, LoadingState, EmptyState, DataTable,
  Pagination as MdPagination, StatusPill, Button, Icon, type PillTone, FilterBar, useConfirm, IconButton,
} from '../components/ui'
import { CommissionsChartCard } from '../components/StatsCharts'
import { OperatorMonthlyCard } from '../components/OperatorMonthlyCard'
import { chartRange } from '../components/chartRange'

/**
 * REQ-206-002: mirrors `PolicyNames.EqualStaff`'s `RequireRole(...)` set (`EqualCrmPolicies.cs`)
 * exactly — a verbatim copy of `ContractFormPage.tsx`'s own `isEqualStaff`, duplicated for the same
 * reason that one gives: it is a small page-local helper, not a shared component, and that page
 * doesn't export it. `auth.role` is a 1:1 client-side echo of the same `UserRole` enum the
 * server-side policy checks (`AuthController.cs`: `role = user.Role.ToString()`), so this is a
 * faithful mirror rather than a guess.
 *
 * Needed here because `GET /api/agent-commissions` is now reachable by an Agent-tier caller
 * (spec §206's "Commerciale" may read their OWN commissions), while every write on that controller
 * deliberately keeps its `EqualStaff`/`Admin` gate ("nessun accesso amministrativo"). Without this
 * check the page would offer an agent "Approva"/"Elimina" buttons on their own rows that can only
 * ever answer 403. It is a UI affordance, never the security boundary — the server refuses those
 * calls regardless of what this renders.
 */
function isEqualStaff(auth: LoginResponse): boolean {
  return [
    'Administrator',
    'EqualAdmin',
    'EqualDirettoreGenerale',
    'EqualDirettoreCommerciale',
    'EqualAreaManager',
    'EqualOperatore',
    'EqualBackOffice',
  ].includes(auth.role)
}

const STATUS_COLORS: Record<string, PillTone> = {
  pending: 'warning',
  approved: 'info',
  paid: 'success',
  cancelled: 'neutral',
}

const STATUS_LABELS: Record<string, string> = {
  pending: 'In Attesa',
  approved: 'Approvata',
  paid: 'Pagata',
  cancelled: 'Annullata',
}

const PAGE_SIZE = 25

function formatEur(n: number): string {
  return '€ ' + n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/** Renders a redacted-amount cell: a lock icon with a tooltip when the backend
 * omitted the field (staff without CanSeeRealAmounts), otherwise the normal
 * formatted amount. */
function AmountCell({ value }: { value: number | undefined }) {
  if (value === undefined) {
    return (
      <span title="Importo non visibile">
        <Icon name="lock" size={18} />
      </span>
    )
  }
  return <>{formatEur(value)}</>
}

function Pagination({
  page,
  total,
  pageSize,
  onPage,
}: {
  page: number
  total: number
  pageSize: number
  onPage: (p: number) => void
}) {
  const totalPages = Math.ceil(total / pageSize)
  if (totalPages <= 1) return null
  return <MdPagination page={page} pageSize={pageSize} total={total} onChange={onPage} />
}

/** F-101: ordinamento di default di GET /api/agent-commissions (creazione decrescente). */
const DEFAULT_SORT = 'created_desc'

export function CommissionsPage() {
  const confirm = useConfirm()
  const [status, setStatus] = useState('')
  // F-101: ordinamento lato server (created_desc = quello di sempre).
  const [sort, setSort] = useState(DEFAULT_SORT)
  const [page, setPage] = useState(1)
  const [data, setData] = useState<Page<AgentCommission> | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // REQ-206-002: this page has no `auth` prop today (App.tsx does not route one through — out of
  // scope here), so it resolves the current session itself with the same call App.tsx already
  // makes on mount (`fetchMe()`, `GET /api/auth/me`). Same pattern, for the same reason, as
  // `ContractFormPage.tsx`'s own `canSetAmounts`. `null` while loading means the administrative
  // buttons stay hidden until the role is known — the safe direction, since showing them to an
  // agent only ever produces a 403.
  const [auth, setAuth] = useState<LoginResponse | null>(null)
  useEffect(() => {
    fetchMe().then(setAuth).catch(() => setAuth(null))
  }, [])
  const canAdminister = auth !== null && isEqualStaff(auth)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const p: { page: number; status?: string; sort: string } = { page, sort }
      if (status) p.status = status
      const result = await agentCommissionsApi.list(p)
      setData(result)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore')
    } finally {
      setLoading(false)
    }
  }, [status, page, sort])

  function handleSort(next: string) { setSort(next); setPage(1) }

  useEffect(() => {
    void load()
  }, [load])

  async function handleApprove(id: number): Promise<void> {
    try {
      await agentCommissionsApi.approve(id)
      void load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore approvazione')
    }
  }

  async function handleDelete(id: number): Promise<void> {
    if (!(await confirm({ title: 'Eliminare la provvigione?', message: 'Eliminare definitivamente questa provvigione?', confirmLabel: 'Elimina', destructive: true }))) return
    try {
      await agentCommissionsApi.delete(id)
      void load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore eliminazione')
    }
  }

  const items = data?.items ?? []
  const total = data?.total ?? 0
  const paidCount = items.filter(c => c.status === 'paid').length
  const pendingCount = items.filter(c => c.status === 'pending').length

  const is403 =
    error !== null &&
    (error.includes('403') ||
      error.toLowerCase().includes('forbidden') ||
      error.toLowerCase().includes('unauthorized'))

  return (
    <PageLayout>
      <PageHeader
        title="Provvigioni"
        icon="savings"
        count={total}
      />

      <FilterBar>
        <Select
          label="Stato"
          value={status}
          onChange={e => {
            setStatus(e.target.value)
            setPage(1)
          }}
        >
          <option value="">Tutti</option>
          <option value="pending">In Attesa</option>
          <option value="approved">Approvata</option>
          <option value="paid">Pagata</option>
          <option value="cancelled">Annullata</option>
        </Select>
        <SortResetLink sort={sort} defaultSort={DEFAULT_SORT} onReset={() => handleSort(DEFAULT_SORT)} />
      </FilterBar>

      {/* Grafico: ultimi 12 mesi, per mese */}
      <CommissionsChartCard range={chartRange()} />

      <OperatorMonthlyCard />

      {/* KPI row */}
      <StatGrid>
        <StatCard label="Totale commissioni" value={total} />
        <StatCard label="Pagate" supporting="pagina corrente" value={paidCount} tone="success" />
        <StatCard label="In attesa" supporting="pagina corrente" value={pendingCount} tone="warning" />
      </StatGrid>

      {/* REQ-206-002: the list itself is no longer staff-only — it is now reachable by any
          authenticated caller and simply returns the rows that caller may see, so the old
          "(solo staff Equal)" hint would be wrong. A 403 surfacing here now means an
          administrative action was refused, or the session is no longer valid. */}
      {is403 && <Callout tone="warning">Accesso non autorizzato</Callout>}
      {error && !is403 && <Callout tone="error">{error}</Callout>}

      {/* Table */}
      {loading ? (
        <LoadingState />
      ) : items.length === 0 ? (
        <EmptyState icon="percent" title="Nessuna provvigione trovata" />
      ) : (
        <>
          <DataTable>
            <thead>
              <tr>
                <SortKeyTh label="ID" ascKey="created_asc" descKey="created_desc" sort={sort} onSort={handleSort} title="Ordina per data di creazione" />
                <th>Contratto</th>
                <th>Agente</th>
                <th>Prodotto</th>
                <th className="md-num">Importo Commissione</th>
                <th className="md-num">Importo Pagato</th>
                <SortKeyTh label="Stato" ascKey="status" sort={sort} onSort={handleSort} />
                {/* REQ-206-002: the whole column disappears for an Agent-tier caller — every
                    action in it is EqualStaff/Admin-gated server-side. */}
                {canAdminister && <th>Azioni</th>}
              </tr>
            </thead>
            <tbody>
              {items.map(c => (
                <tr key={c.id}>
                  <td className="font-monospace">#{c.id}</td>
                  <td>#{c.contractId}</td>
                  <td>#{c.userId}</td>
                  <td>{c.productId != null ? `#${c.productId}` : '—'}</td>
                  <td className="md-num"><AmountCell value={c.commissionAmount} /></td>
                  <td className="md-num"><AmountCell value={c.paidAmount} /></td>
                  <td>
                    <StatusPill tone={STATUS_COLORS[c.status] ?? 'neutral'}>
                      {STATUS_LABELS[c.status] ?? c.status}
                    </StatusPill>
                  </td>
                  {canAdminister && (
                    <td>
                      <div className="d-flex gap-1">
                        {c.status === 'pending' && (
                          <Button
                            variant="outlined"
                            size="sm"
                            icon="check_circle"
                            onClick={() => void handleApprove(c.id)}
                          >
                            Approva
                          </Button>
                        )}
                        {(c.status === 'pending' || c.status === 'cancelled') && (
                          <IconButton
                            icon="delete"
                            size="sm"
                            aria-label="Elimina"
                            onClick={() => void handleDelete(c.id)}
                          />
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </DataTable>
          <div className="d-flex justify-content-center">
            <Pagination page={page} total={total} pageSize={PAGE_SIZE} onPage={setPage} />
          </div>
        </>
      )}
    </PageLayout>
  )
}
